import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Menu, MenuItem, MenuItemLookupResult, OrderApiException, OrderErrorCode, OrderType, PortionItemType, RecentOrFavoriteEntree } from '@/models';
import { arrayToMap, formatEntreeContents, getAllMenuItems, isDefined, joinWithSuffix } from '@/helpers';
import { Routes } from '@/router';
import { RecentOneTapEntree } from '../cmg-meal-builder-one-tap-reorder';
import { convertRecentAndFavEntreeToOrderMealRequest } from '@/services/order/order-helper';
import { formatRecentEntreeName } from '@/services/recent-and-favorite-orders/recent-and-favorite-orders-helper';

const MAX_LENGTH_OF_DETAILS = 75;
@Component({
  name: 'CmgMealBuilderOneTapReorderCard',
})
export default class CmgMealBuilderOneTapReorderCard extends Vue {
  @Prop({ required: true, type: Object })
  recentEntree!: RecentOneTapEntree;

  @Prop({ required: true, type: Object })
  menu!: Menu;
  
  unavailableItems: MenuItem[] = [];

  get menuItemLookupMap(): Map<string, MenuItemLookupResult> | null {
    return this.$appStore.state.menu.menuItemLookupMap;
  }

  get orderDate(): string {
    return this.recentEntree.orderDate
  }

  get formattedOrderEntreeDetails(): string {
    if (!this.recentEntree) {
      return '';
    }

    let items = formatEntreeContents(this.recentEntree.entree, this.menuItemLookupMap)
    const suffix = items.length == 2 ? ' and ' : ', and ';

    if (`${items.join(', ')}${suffix}`.length > MAX_LENGTH_OF_DETAILS) {
      return items.join(', ').substring(0, MAX_LENGTH_OF_DETAILS).trim() + '...';
    }

    return joinWithSuffix(items, ', ', suffix);
  }

  get menuItemName(): string {
    return formatRecentEntreeName(this.recentEntree.entree, this.menuItemLookupMap);
  }

  get hasUnavailableItems(): boolean {
    return this.unavailableItems.length > 0;
  }

  mounted() {
    if (this.recentEntree.entree.contents) {
      const allItems = arrayToMap(getAllMenuItems(this.menu, true, true), i => i.itemId);
      
      this.unavailableItems = this.recentEntree.entree.contents.map(ec => allItems[ec.menuItemId]).filter(isDefined).filter(i => !i.isItemAvailable);

      const entreeItem = allItems[this.recentEntree.entree.menuItemId];
      if (entreeItem && !entreeItem.isItemAvailable) {
        this.unavailableItems.push(entreeItem);
      }
    }
  }

  onClickViewDetails(): void {
    this.$services.ui.setModalVisibility('oneTapReorderDetailsModal', true, {
      orderDate: this.orderDate,
      entree: this.recentEntree.entree,
      onAddToBag: () => this.onClickAddItem()
    });
  }

  async onClickAddItem(updatedEntree?: RecentOrFavoriteEntree): Promise<void> { 
      try {
        if (this.recentEntree && !updatedEntree && this.hasUnavailableItems) {
          this.$services.ui.setModalVisibility('oneTapReorderUnavailabilityModal', true, {
            entree: this.recentEntree.entree,
            unavailableItems: this.unavailableItems,
            onAddToBag: (updatedEntree: RecentOrFavoriteEntree) => this.onClickAddItem(updatedEntree)
          });
        } else {
          await this.$services.monoMenu.awaitCacheItemsAndBuildMenu();
          this.addToBag(updatedEntree);
        }
      } catch (e) {
        throw e;
      }
  }

  async addToBag(updatedEntree?: RecentOrFavoriteEntree) {
    try {
      const order = this.$services.order.getCurrentOrder() || (await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant!.restaurantNumber, OrderType.Regular))!;
      const orderMealRequest = convertRecentAndFavEntreeToOrderMealRequest(updatedEntree ?? this.recentEntree.entree!);
      const preFilledMealName = this.$appStore.state.customer.profile?.firstName || '';
      
      this.$services.ui.setModalVisibility('mealBuilderMealName', true, {
        beforeClose: async mealName => {
          orderMealRequest.meal.mealName = mealName;
          try {
            await this.$services.order.addMealToOrder(order, orderMealRequest);
            this.$services.bag.setBagVisibility(true);
            this.$services.ui.setLastSelectedElement('bag');
            Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
            this.$router.replace({ name: Routes.Home });
          } catch(e) {
            if (e instanceof OrderApiException && e.errors[0].errorCode == OrderErrorCode.MaxEntreeLimitExceeded) {
              this.$services.ui.setModalVisibility('maxEntreeLimitModal', true, { maxEntreeLimitNumber: e.errors[0].errorData });
            } else {
              throw e;
            }
          }
        },
        mealName: preFilledMealName,
      });
    } catch (e) {
        throw e;
    }
  }
}
